export const DEMO_TEMPLATE_IDS = [
  "bank-statements",
  "purchase-orders",
  "invoices",
  "receipts",
  "document-parser",
  "blank-space",
] as const;
export type DemoTemplateId = (typeof DEMO_TEMPLATE_IDS)[number];
export type DemoField = {
  name: string;
  type: "text" | "numeric" | "integer" | "date" | "foreign_key";
  prompt: string;
  referencedTable?: string;
};
export type DemoTable = {
  name: string;
  fields: DemoField[];
  rows: Record<string, string | number>[];
  children?: DemoTable[];
};
export type DemoTemplate = {
  id: DemoTemplateId;
  name: string;
  description: string;
  primaryTable: string | null;
  tables: DemoTable[];
  queries: { name: string; sql: string }[];
};

const field = (
  name: string,
  type: DemoField["type"] = "text",
  referencedTable?: string,
): DemoField => ({
  name,
  type,
  referencedTable,
  prompt: `Extract ${name.replace(/_/g, " ")} from this document.${referencedTable ? ` Resolve the matching record in ${referencedTable}.` : ""}`,
});
const fields = (
  names: string[],
  numeric: string[] = [],
  dates: string[] = [],
): DemoField[] =>
  names.map((name) =>
    field(
      name,
      numeric.includes(name)
        ? "numeric"
        : dates.includes(name)
          ? "date"
          : "text",
    ),
  );
const items = (total: string): DemoTable => ({
  name: "items",
  fields: fields(
    ["description", "quantity", "unit_price", total],
    ["quantity", "unit_price", total],
  ),
  rows: [1, 2, 3].map((id) => ({
    description: ["Storage boxes", "Packing supplies", "Shipping labels"][
      id - 1
    ]!,
    quantity: id * 2,
    unit_price: 25,
    [total]: id * 50,
  })),
});
const commercial = (
  id: "invoices" | "purchase-orders" | "receipts",
  parent: string,
  parentFields: DemoField[],
  name: string,
  description: string,
  main: string,
  mainFields: DemoField[],
  itemName: string,
): DemoTemplate => {
  const detail = items(id === "receipts" ? "item_total" : "line_total");
  detail.name = itemName;
  const parentRows = [1, 2, 3].map((index) => ({
    name: ["Harbor Supply Co.", "Atlas Goods Ltd.", "Northstar Logistics"][
      index - 1
    ]!,
    address: `${index * 10} Market Street`,
    contact_email: `orders${index}@example.com`,
    tax_identifier: `SAMPLE-${index}`,
  }));
  const rows = [1, 2, 3].map(
    (index) =>
      Object.fromEntries(
        mainFields.map((column) => [
          column.name,
          column.type === "foreign_key"
            ? index
            : column.type === "numeric"
              ? ["total", "subtotal"].includes(column.name)
                ? index * 50
                : 0
              : column.type === "date"
                ? `2026-09-0${index}`
                : column.name === "currency"
                  ? "USD"
                  : column.name === "transaction_time"
                    ? "10:15:00"
                    : column.name === "payment_method"
                      ? "Card"
                      : `DEMO-${index.toString().padStart(3, "0")}`,
        ]),
      ) as Record<string, string | number>,
  );
  return {
    id,
    name,
    description,
    primaryTable: main,
    tables: [
      { name: parent, fields: parentFields, rows: parentRows },
      { name: main, fields: mainFields, rows, children: [detail] },
    ],
    queries: [
      {
        name: "Latest documents",
        sql: `SELECT * FROM ${main} ORDER BY id DESC LIMIT 20`,
      },
      {
        name: "Totals by company",
        sql: `SELECT p.name, SUM(d.total) AS total FROM ${main} d JOIN ${parent} p ON p.id = d.${parent === "merchants" ? "merchant" : parent === "suppliers" ? "supplier" : "vendor"}_id GROUP BY p.name`,
      },
    ],
  };
};

export const DEMO_TEMPLATES: DemoTemplate[] = [
  {
    id: "bank-statements",
    name: "Bank statements",
    description:
      "Explore accounts, statements, and transactions in one workspace.",
    primaryTable: "statements",
    tables: [
      {
        name: "accounts",
        fields: fields([
          "institution",
          "account_holder",
          "account_identifier",
          "currency",
        ]),
        rows: [1, 2, 3].map((id) => ({
          institution: "Cedar Commercial Bank",
          account_holder: [
            "Northstar Logistics",
            "Atlas Goods Ltd.",
            "Harbor Supply Co.",
          ][id - 1]!,
          account_identifier: `ending ${4820 + id}`,
          currency: "USD",
        })),
      },
      {
        name: "statements",
        fields: [
          field("account_id", "foreign_key", "accounts"),
          ...fields(
            [
              "period_start",
              "period_end",
              "opening_balance",
              "closing_balance",
            ],
            ["opening_balance", "closing_balance"],
            ["period_start", "period_end"],
          ),
        ],
        rows: [1, 2, 3].map((id) => ({
          account_id: id,
          period_start: "2026-09-01",
          period_end: "2026-09-30",
          opening_balance: 5000 * id,
          closing_balance: 5000 * id - 125 * id,
        })),
        children: [
          {
            name: "transactions",
            fields: fields(
              [
                "transaction_date",
                "posting_date",
                "description",
                "reference",
                "debit",
                "credit",
                "balance",
              ],
              ["debit", "credit", "balance"],
              ["transaction_date", "posting_date"],
            ),
            rows: [1, 2, 3].map((id) => ({
              transaction_date: `2026-09-0${id}`,
              posting_date: `2026-09-0${id}`,
              description: "Office supplies",
              reference: `TX-DEMO-${id}`,
              debit: 125 * id,
              credit: 0,
              balance: 5000 * id - 125 * id,
            })),
          },
        ],
      },
    ],
    queries: [
      {
        name: "Account balances",
        sql: "SELECT a.account_holder, s.period_end, s.closing_balance FROM statements s JOIN accounts a ON a.id = s.account_id ORDER BY s.period_end DESC",
      },
      {
        name: "Transactions",
        sql: "SELECT transaction_date, description, debit, credit FROM transactions ORDER BY transaction_date DESC LIMIT 20",
      },
    ],
  },
  commercial(
    "purchase-orders",
    "suppliers",
    fields(["name", "address", "contact_email"]),
    "Purchase orders",
    "Turn orders into connected supplier and line-item records.",
    "purchase_orders",
    [
      field("supplier_id", "foreign_key", "suppliers"),
      ...fields(
        [
          "purchase_order_number",
          "order_date",
          "expected_delivery_date",
          "currency",
          "total",
        ],
        ["total"],
        ["order_date", "expected_delivery_date"],
      ),
    ],
    "purchase_order_items",
  ),
  commercial(
    "invoices",
    "vendors",
    fields(["name", "address", "tax_identifier"]),
    "Invoices",
    "Extract invoice details, vendor records, and line items.",
    "invoices",
    [
      field("vendor_id", "foreign_key", "vendors"),
      ...fields(
        [
          "invoice_number",
          "invoice_date",
          "due_date",
          "currency",
          "subtotal",
          "tax",
          "total",
        ],
        ["subtotal", "tax", "total"],
        ["invoice_date", "due_date"],
      ),
    ],
    "invoice_line_items",
  ),
  commercial(
    "receipts",
    "merchants",
    fields(["name", "address"]),
    "Receipts",
    "Capture merchants, totals, and purchased items from receipts.",
    "receipts",
    [
      field("merchant_id", "foreign_key", "merchants"),
      ...fields(
        [
          "transaction_date",
          "transaction_time",
          "payment_method",
          "subtotal",
          "tax",
          "tip",
          "total",
        ],
        ["subtotal", "tax", "tip", "total"],
        ["transaction_date"],
      ),
    ],
    "receipt_items",
  ),
  {
    id: "document-parser",
    name: "Document parser",
    description: "Connect shipping invoices with shipment tracking records.",
    primaryTable: "shipping_invoices",
    tables: [
      {
        name: "shipping_invoices",
        fields: fields(
          [
            "invoice_number",
            "shipment_reference",
            "tracking_number",
            "carrier",
            "shipper_name",
            "consignee_name",
            "origin",
            "destination",
            "issue_date",
            "expected_delivery",
            "currency",
            "total_amount",
            "status",
          ],
          ["total_amount"],
          ["issue_date", "expected_delivery"],
        ),
        rows: [1, 2, 3].map((id) => ({
          invoice_number: `SHIP-DEMO-${id}`,
          shipment_reference: `REF-${id}`,
          tracking_number: `TRACK-${id}`,
          carrier: "Sample Logistics",
          shipper_name: "Harbor Supply Co.",
          consignee_name: "Atlas Goods Ltd.",
          origin: "Kingston",
          destination: "Miami",
          issue_date: `2026-09-0${id}`,
          expected_delivery: `2026-09-1${id}`,
          currency: "USD",
          total_amount: 200 * id,
          status: "Delivered",
        })),
        children: [
          {
            name: "tracking_shipments",
            fields: fields(
              [
                "tracking_number",
                "checkpoint_sequence",
                "event_time",
                "event_status",
                "location",
                "notes",
              ],
              ["checkpoint_sequence"],
            ),
            rows: [1, 2, 3].map((id) => ({
              tracking_number: `TRACK-${id}`,
              checkpoint_sequence: 1,
              event_time: `2026-09-1${id}T10:00:00Z`,
              event_status: "Delivered",
              location: "Miami",
              notes: "Sample delivery confirmation",
            })),
          },
        ],
      },
    ],
    queries: [
      {
        name: "Shipment tracking",
        sql: "SELECT i.invoice_number, t.tracking_number, t.event_status, t.location FROM shipping_invoices i JOIN tracking_shipments t ON t.internal_fk_shipping_invoices_id = i.id",
      },
    ],
  },
  {
    id: "blank-space",
    name: "Blank space",
    description:
      "Start with your own documents, tables, and extraction fields.",
    primaryTable: null,
    tables: [],
    queries: [],
  },
];

export function getDemoTemplate(value: string): DemoTemplate | undefined {
  const canonical =
    value === "invoice"
      ? "invoices"
      : value === "default"
        ? "blank-space"
        : value;
  return DEMO_TEMPLATES.find((template) => template.id === canonical);
}
